import { atom } from "jotai";
import { createDerivedWritableAtom } from "../../../../utils";
import { videoSDKAtom } from "../../atoms";
import { RemoteParticipant } from "./RemoteParticipant";
import { RemoteMicrophoneByParticipantId } from "./types";
import {
  filterActiveParticipantIds,
  getRemoteParticipantMicrophone,
  parseIdentity,
} from "./utils";

export const participantsAtom = createDerivedWritableAtom(
  videoSDKAtom,
  "participants",
);

export const remoteParticipantConnectionStatesByIdAtom =
  createDerivedWritableAtom(
    videoSDKAtom,
    "remoteParticipantConnectionStatesByParticipantId",
  );

export const participantSidsAtom = atom(
  (get) => get(participantsAtom)?.map((p) => p.sid) ?? [],
);

export const participantIdsAtom = atom(
  (get) => get(participantsAtom)?.map((p) => p.identity) ?? [],
);

export const parsedParticipantIdsAtom = atom((get) =>
  get(participantIdsAtom).map((identity) => parseIdentity(identity)),
);

export const participantUserIdsAtom = atom((get) =>
  get(parsedParticipantIdsAtom).map(({ id }) => id),
);

export const participantsByIdAtom = atom<Record<string, RemoteParticipant>>(
  (get) =>
    get(participantsAtom)?.reduce(
      (agg, p) => ({ ...agg, [parseIdentity(p.identity).id]: p }),
      {},
    ) ?? {},
);

export const participantCountAtom = atom(
  (get) => get(participantsAtom)?.length ?? 0,
);

// ================================================================================

export const remoteAudioTrackStatsBySidAtom = createDerivedWritableAtom(
  videoSDKAtom,
  "remoteAudioTrackStatsBySid",
);

export const remoteMediaByParticipantIdAtom = createDerivedWritableAtom(
  videoSDKAtom,
  "remoteMediaByParticipantId",
);

export const activelySpeakingParticipantIdsAtom = atom((get) => {
  const participantIds = get(participantIdsAtom);
  const remoteMediaByParticipantId = get(remoteMediaByParticipantIdAtom);
  const remoteAudioTrackStatsBySid = get(remoteAudioTrackStatsBySidAtom);

  const remoteMicrophoneByParticipantId =
    participantIds.reduce<RemoteMicrophoneByParticipantId>(
      (acc, participantId) => {
        const remoteMicrophone = getRemoteParticipantMicrophone(
          remoteMediaByParticipantId,
          participantId,
        );
        if (!remoteMicrophone) return acc;
        return {
          ...acc,
          [participantId]: remoteMicrophone,
        };
      },
      {},
    );

  return filterActiveParticipantIds(
    remoteMicrophoneByParticipantId,
    remoteAudioTrackStatsBySid,
  );
});

export const remoteVideoDelegatorsAtom = atom((get) => {
  const remoteMediaByParticipantId = get(remoteMediaByParticipantIdAtom);
  return Object.values(remoteMediaByParticipantId).flatMap(
    (delegators) => delegators.video ?? [],
  );
});

export const remoteAudioDelegatorsAtom = atom((get) => {
  const remoteMediaByParticipantId = get(remoteMediaByParticipantIdAtom);
  return Object.values(remoteMediaByParticipantId).flatMap(
    (delegators) => delegators.audio ?? [],
  );
});

export const remoteDataDelegatorsAtom = atom((get) => {
  const remoteMediaByParticipantId = get(remoteMediaByParticipantIdAtom);
  return Object.values(remoteMediaByParticipantId).flatMap(
    (delegators) => delegators.data ?? [],
  );
});
